// Tabelas de referência da bateria de testes de capacidade funcional do idoso (Rikli & Jones).
// Fonte única usada tanto pela tela (idosos/TesteCapacidadeFuncional.js) quanto pelo relatório em PDF.

export const FAIXAS_IDADE_CF = ['60-64', '65-69', '70-74', '75-79', '80-84', '85-89', '90-94'];

// referencias: intervalo esperado [mínimo, máximo] por faixa etária, na ordem de FAIXAS_IDADE_CF.
// melhor: 'maior' → desempenho melhor quando o valor sobe; 'menor' → melhor quando o valor cai (tempo).
export const TESTES_CF = [
  {
    chave: 'flexaoCotovelo',
    nome: 'Flexão e extensão de cotovelo',
    resultado: 'Número de repetições',
    unidade: 'repetições',
    melhor: 'maior',
    referencias: {
      feminino: [[13, 19], [12, 18], [12, 17], [11, 17], [10, 16], [10, 15], [8, 13]],
      masculino: [[16, 22], [15, 21], [14, 21], [13, 19], [13, 19], [11, 17], [10, 14]],
    },
  },
  {
    chave: 'levantarCadeira',
    nome: 'Levantar e sentar da cadeira em 30 segundos',
    resultado: 'Número de repetições',
    unidade: 'repetições',
    melhor: 'maior',
    referencias: {
      feminino: [[12, 17], [11, 16], [10, 15], [10, 15], [9, 14], [8, 13], [4, 11]],
      masculino: [[14, 19], [12, 18], [12, 17], [11, 17], [10, 15], [8, 14], [7, 12]],
    },
  },
  {
    chave: 'testeMarcha',
    nome: 'Marcha estacionária de 2 minutos',
    resultado: 'Número de passos',
    unidade: 'passos',
    melhor: 'maior',
    referencias: {
      feminino: [[75, 107], [73, 107], [68, 101], [68, 100], [60, 90], [55, 85], [44, 72]],
      masculino: [[87, 115], [86, 116], [80, 110], [73, 109], [71, 103], [59, 91], [52, 86]],
    },
  },
  {
    chave: 'alcancarPes',
    nome: 'Sentar e alcançar os pés',
    resultado: 'Score',
    unidade: 'cm',
    melhor: 'maior',
    referencias: {
      feminino: [[-1, 13], [-1, 11], [-3, 10], [-4, 9], [-5, 8], [-6, 6], [-11, 3]],
      masculino: [[-6, 10], [-8, 8], [-9, 6], [-10, 5], [-14, 4], [-14, 1], [-17, 1]],
    },
  },
  {
    chave: 'levantarCaminhar',
    nome: 'Levantar e caminhar',
    resultado: 'Score',
    unidade: 'segundos',
    melhor: 'menor',
    referencias: {
      feminino: [[4.4, 6.0], [4.8, 6.4], [4.9, 7.1], [5.2, 7.4], [5.7, 8.7], [6.2, 9.6], [7.3, 11.5]],
      masculino: [[3.8, 5.6], [4.3, 5.7], [4.2, 6.0], [4.6, 7.2], [5.2, 7.6], [5.3, 8.9], [6.2, 10.0]],
    },
  },
  {
    chave: 'alcancarCostas',
    nome: 'Alcançar atrás das costas',
    resultado: 'Score',
    unidade: 'cm',
    melhor: 'maior',
    referencias: {
      feminino: [[-8, 4], [-9, 4], [-10, 3], [-13, 1], [-14, 0], [-18, -3], [-20, -3]],
      masculino: [[-17, 0], [-19, -3], [-20, -3], [-23, -5], [-24, -5], [-25, -8], [-27, -10]],
    },
  },
  {
    chave: 'caminhada6min',
    nome: 'Caminhada de 6 minutos',
    resultado: 'Distância percorrida',
    unidade: 'metros',
    melhor: 'maior',
    referencias: {
      feminino: [[498, 604], [457, 581], [439, 562], [393, 535], [352, 494], [311, 466], [251, 402]],
      masculino: [[578, 672], [512, 640], [498, 622], [430, 585], [407, 553], [348, 521], [279, 457]],
    },
  },
];

export const CORES_CLASSIFICACAO_CF = {
  'Abaixo do esperado': '#ff5e00',
  'Dentro do esperado': '#32CD32',
  'Acima do esperado': '#00BFFF',
};

// Índice da faixa etária do avaliado; -1 quando a idade está fora das tabelas (60 a 94 anos).
export const indiceFaixaIdadeCF = (idade) => {
  const i = Number(idade);
  if (!Number.isFinite(i) || i < 60 || i > 94) return -1;
  return Math.min(FAIXAS_IDADE_CF.length - 1, Math.floor((i - 60) / 5));
};

export const ehSexoMasculinoCF = (sexo) => String(sexo || '').trim().toLowerCase().startsWith('m');

// Intervalo esperado para o avaliado; null quando não há referência para a idade informada.
export const referenciaCF = (teste, sexo, idade) => {
  const idx = indiceFaixaIdadeCF(idade);
  if (idx < 0) return null;
  const linha = ehSexoMasculinoCF(sexo) ? teste.referencias.masculino : teste.referencias.feminino;
  return linha[idx] || null;
};

// Formata o intervalo: testes com valores negativos usam "-8 a +4"; os demais, "13 - 19".
export const formatarFaixaCF = (faixa) => {
  if (!faixa) return '—';
  const [min, max] = faixa;
  if (min < 0 || max < 0) {
    const sinal = (n) => (n > 0 ? `+${n}` : `${n}`);
    return `${sinal(min)} a ${sinal(max)}`;
  }
  return `${min} - ${max}`;
};

export const classificarCF = (teste, valor, sexo, idade) => {
  // Campo em branco não classifica (Number('') seria 0 e cairia numa faixa indevida).
  if (valor === undefined || valor === null || String(valor).trim() === '') return null;
  const n = Number(String(valor).replace(',', '.'));
  const faixa = referenciaCF(teste, sexo, idade);
  if (!Number.isFinite(n) || !faixa) return null;
  const [min, max] = faixa;
  if (n >= min && n <= max) return 'Dentro do esperado';
  if (teste.melhor === 'menor') return n < min ? 'Acima do esperado' : 'Abaixo do esperado';
  return n > max ? 'Acima do esperado' : 'Abaixo do esperado';
};
